/**
 * SuperAdminFamilyCoursePage — read-only stats dashboard for BOTH course
 * drip funnels (family-aim + individual-arp).  Route: /superadmin/family-course.
 *
 * A stream toggle at the top lets the SuperAdmin switch between:
 *   - AIM Family Course      (course_stream=family-aim)
 *   - ARP Individual Course  (course_stream=individual-arp)
 *
 * Wraps GET /api/admin/family-course/stats?stream=... and renders totals,
 * per-UTM breakdown, day-by-day funnel, Day-5 CTR, and marketing consent
 * counter.  Data is fetched anew on each stream switch.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/authFetch';
import { ArrowLeft, Users, Mail, MousePointerClick, UserX, TrendingUp, Loader2 } from 'lucide-react';

const STREAMS = [
  { id: 'family-aim', label: 'AIM Family Course', signupPath: '/family-course' },
  { id: 'individual-arp', label: 'ARP Individual Course', signupPath: '/pleasure-or-problem' },
];

function StatCard({ icon: Icon, label, value, tone = 'zinc', testId }) {
  const tones = {
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div className={`${tones[tone]} border rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 opacity-70">
        <Icon className="w-4 h-4" />
        <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function SuperAdminFamilyCoursePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedStream, setSelectedStream] = useState('family-aim');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async (streamId) => {
    setLoading(true);
    setError('');
    try {
      const resp = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/family-course/stats?stream=${streamId}`,
      );
      if (!resp.ok) throw new Error(`Stats request failed: ${resp.status}`);
      setStats(await resp.json());
    } catch (e) {
      setError(e.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchStats(selectedStream);
  }, [user, navigate, selectedStream, fetchStats]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 p-6">
        <div className="max-w-3xl mx-auto bg-white border border-red-200 rounded-xl p-6">
          <p className="text-sm text-red-700" data-testid="family-course-stats-error">{error}</p>
        </div>
      </div>
    );
  }

  const dayCounts = stats?.subscribers_by_current_day || {};
  const utmCounts = stats?.subscribers_by_utm_source || {};
  const dayLabels = ['Signed up (Day 0)', 'Day 1 sent', 'Day 2 sent', 'Day 3 sent', 'Day 4 sent', 'Day 5 sent'];

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/superadmin')}
            className="p-2 hover:bg-zinc-100 rounded-lg"
            data-testid="family-course-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-zinc-900 truncate">
              {STREAMS.find(s => s.id === selectedStream)?.label || 'Course Analytics'}
            </h1>
            <p className="text-[11px] text-zinc-500">
              Drip funnel: stream <code>{selectedStream}</code>
            </p>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto">
          {STREAMS.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedStream(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                selectedStream === s.id
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
              }`}
              data-testid={`stream-toggle-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={Users} label="Total sign-ups" value={stats.total_subscribers} tone="zinc" testId="stat-total" />
          <StatCard icon={Mail} label="Active" value={stats.active_subscribers} tone="emerald" testId="stat-active" />
          <StatCard icon={UserX} label="Unsubscribed" value={stats.unsubscribed} tone="rose" testId="stat-unsubscribed" />
          <StatCard icon={TrendingUp} label="Completed Day 5" value={stats.completed_day5} tone="blue" testId="stat-completed" />
          <StatCard icon={MousePointerClick} label="Day-5 CTR" value={`${stats.day5_ctr_percent}%`} tone="amber" testId="stat-ctr" />
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="family-course-per-day">
          <h2 className="text-sm font-bold text-zinc-900 mb-4">Day-by-day funnel</h2>
          <div className="space-y-2">
            {dayLabels.map((label, idx) => {
              const count = dayCounts[String(idx)] || 0;
              const pct = stats.total_subscribers ? Math.round((count / stats.total_subscribers) * 100) : 0;
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-32 flex-shrink-0 text-xs text-zinc-600">{label}</div>
                  <div className="flex-1 bg-zinc-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-zinc-900 h-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-xs font-semibold text-zinc-800">
                    {count} ({pct}%)
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="family-course-per-utm">
          <h2 className="text-sm font-bold text-zinc-900 mb-4">Sign-ups by UTM source</h2>
          {Object.keys(utmCounts).length === 0 ? (
            <p className="text-xs text-zinc-500">No sign-ups yet.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(utmCounts).map(([source, count]) => {
                const pct = stats.total_subscribers ? Math.round((count / stats.total_subscribers) * 100) : 0;
                return (
                  <div key={source} className="flex items-center gap-3">
                    <div className="w-28 flex-shrink-0 text-xs text-zinc-600 capitalize">{source}</div>
                    <div className="flex-1 bg-zinc-100 rounded-full h-4 overflow-hidden">
                      <div className="bg-blue-600 h-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-16 text-right text-xs font-semibold text-zinc-800">
                      {count} ({pct}%)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="family-course-consent-box">
          <h2 className="text-sm font-bold text-zinc-900 mb-2">Secondary marketing consent</h2>
          <p className="text-xs text-zinc-600 leading-relaxed">
            <strong>{stats.secondary_marketing_consented}</strong> of {stats.total_subscribers} sign-ups
            ticked the "keep me updated about AIM" box. Only these subscribers may be added to the AIM
            marketing list once the course finishes. The other {stats.total_subscribers - stats.secondary_marketing_consented} did
            not consent and must be excluded from any AIM cross-promotion beyond the Day-5 email itself.
          </p>
        </section>
      </main>
    </div>
  );
}
